/* ==========================================================================
   The Lazy Butcher — global.js
   Theme toggle, scroll reveal, hero crossfade, mobile nav, popup, form handlers.
   ========================================================================== */

(function () {
  'use strict';

  const T = window.theme || {};
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const $ = (sel, root = document) => root.querySelector(sel);
  const escapeHtml = (value) => String(value || '').replace(/[&<>"']/g, (char) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  }[char]));
  const MONTHS = {
    JAN: 0, JANUARY: 0,
    FEB: 1, FEBRUARY: 1,
    MAR: 2, MARCH: 2,
    APR: 3, APRIL: 3,
    MAY: 4,
    JUN: 5, JUNE: 5,
    JUL: 6, JULY: 6,
    AUG: 7, AUGUST: 7,
    SEP: 8, SEPT: 8, SEPTEMBER: 8,
    OCT: 9, OCTOBER: 9,
    NOV: 10, NOVEMBER: 10,
    DEC: 11, DECEMBER: 11,
  };
  const stripHtml = (value) => {
    const el = document.createElement('div');
    el.innerHTML = value || '';
    return (el.textContent || el.innerText || '').trim();
  };
  const icsEscape = (value) => String(value || '')
    .replace(/\\/g, '\\\\')
    .replace(/\n/g, '\\n')
    .replace(/,/g, '\\,')
    .replace(/;/g, '\\;');
  const pad2 = (value) => String(value).padStart(2, '0');
  const formatIcsDate = (date) => `${date.getFullYear()}${pad2(date.getMonth() + 1)}${pad2(date.getDate())}`;
  const formatIcsDateTime = (date) => `${formatIcsDate(date)}T${pad2(date.getHours())}${pad2(date.getMinutes())}00`;
  const parseEventTime = (value) => {
    const text = String(value || '').trim();
    if (!text) return null;
    const match = text.match(/\b(\d{1,2})(?::(\d{2}))?\s*(AM|PM)?\b/i);
    if (!match) return null;
    let hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2] || '0', 10);
    const meridian = (match[3] || '').toUpperCase();
    if (meridian === 'PM' && hours < 12) hours += 12;
    if (meridian === 'AM' && hours === 12) hours = 0;
    if (hours > 23 || minutes > 59) return null;
    return { hours, minutes };
  };
  const parseEventDate = (data) => {
    const exact = String(data.calendarDate || data.date || '').trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(exact)) {
      const [year, month, day] = exact.split('-').map(Number);
      return new Date(year, month - 1, day);
    }

    const monthName = String(data.month || '').trim().toUpperCase();
    const day = parseInt(data.day || '', 10);
    if (!Number.isFinite(day) || !MONTHS.hasOwnProperty(monthName)) return null;

    const today = new Date();
    let date = new Date(today.getFullYear(), MONTHS[monthName], day);
    const yesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
    if (date < yesterday) date = new Date(today.getFullYear() + 1, MONTHS[monthName], day);
    return date;
  };
  const buildCalendarData = (data) => {
    const eventDate = parseEventDate(data);
    if (!eventDate) return null;

    const subtitle = String(data.subtitle || '');
    const startTime = parseEventTime(data.calendarStart || data.start || subtitle);
    const endTime = parseEventTime(data.calendarEnd || data.end || '');
    const location = data.location || (subtitle.includes('·') ? subtitle.split('·').slice(1).join('·').trim() : '');
    const description = stripHtml(data.description || data.content || '');
    const title = String(data.title || 'Lazy Butcher event').trim();
    const uid = `${formatIcsDate(eventDate)}-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-') || 'event'}@lazybutcher.com`;
    const stamp = new Date();
    const nowUtc = `${stamp.getUTCFullYear()}${pad2(stamp.getUTCMonth() + 1)}${pad2(stamp.getUTCDate())}T${pad2(stamp.getUTCHours())}${pad2(stamp.getUTCMinutes())}${pad2(stamp.getUTCSeconds())}Z`;

    const lines = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Lazy Butcher//Events//EN',
      'CALSCALE:GREGORIAN',
      'BEGIN:VEVENT',
      `UID:${icsEscape(uid)}`,
      `DTSTAMP:${nowUtc}`,
      `SUMMARY:${icsEscape(title)}`,
    ];

    if (startTime) {
      const start = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate(), startTime.hours, startTime.minutes);
      const end = endTime
        ? new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate(), endTime.hours, endTime.minutes)
        : new Date(start.getTime() + (2 * 60 * 60 * 1000));
      if (end <= start) end.setDate(end.getDate() + 1);
      lines.push(`DTSTART;TZID=America/Denver:${formatIcsDateTime(start)}`);
      lines.push(`DTEND;TZID=America/Denver:${formatIcsDateTime(end)}`);
    } else {
      const nextDay = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate() + 1);
      lines.push(`DTSTART;VALUE=DATE:${formatIcsDate(eventDate)}`);
      lines.push(`DTEND;VALUE=DATE:${formatIcsDate(nextDay)}`);
    }

    if (location) lines.push(`LOCATION:${icsEscape(location)}`);
    if (description) lines.push(`DESCRIPTION:${icsEscape(description)}`);
    if (data.url) lines.push(`URL:${icsEscape(data.url)}`);
    lines.push('END:VEVENT', 'END:VCALENDAR');

    return {
      href: `data:text/calendar;charset=utf-8,${encodeURIComponent(lines.join('\r\n'))}`,
      filename: data.filename || `${title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'lazy-butcher-event'}.ics`,
    };
  };

  const setReveal = (el, type, delay = 0) => {
    if (!el || el.dataset.revealPrepared === 'true') return;
    el.dataset.reveal = type || el.getAttribute('data-reveal') || 'up';
    if (delay) {
      el.style.setProperty('--reveal-delay', `${delay}ms`);
      el.style.setProperty('--bg-reveal-delay', `${delay + 140}ms`);
    }
    el.dataset.revealPrepared = 'true';
  };

  const applyRevealPattern = (root, pattern = 'default') => {
    if (!root) return;
    const items = $$('[data-reveal]', root);
    if (!items.length) return;

    if (pattern === 'order-reverse') {
      items.slice().reverse().forEach((el, i) => setReveal(el, 'order-right', Math.min(i * 95, 475)));
      return;
    }

    if (pattern === 'alternating') {
      items.forEach((el, i) => setReveal(el, i % 2 ? 'right' : 'left', Math.min(i * 90, 450)));
      return;
    }

    if (pattern === 'tiles-after-hero') {
      items.forEach((el, i) => setReveal(el, i % 2 ? 'right' : 'left', 760 + Math.min(i * 150, 600)));
      return;
    }

    if (pattern === 'gallery') {
      items.forEach((el, i) => setReveal(el, i % 3 === 0 ? 'soft-blur' : 'zoom', Math.min(i * 55, 440)));
      return;
    }

    if (pattern === 'cards') {
      items.forEach((el, i) => setReveal(el, i % 2 ? 'tilt' : 'pop', Math.min(i * 75, 450)));
      return;
    }

    if (pattern === 'editorial') {
      items.forEach((el, i) => setReveal(el, i % 3 === 0 ? 'clip-up' : 'soft-blur', Math.min(i * 85, 425)));
      return;
    }

    items.forEach((el, i) => setReveal(el, el.getAttribute('data-reveal') || 'up', Math.min(i * 60, 360)));
  };

  const revealNow = (root) => {
    if (!root) return;
    $$('[data-reveal]', root).forEach((el, i) => {
      window.setTimeout(() => el.classList.add('is-visible'), prefersReduced ? 0 : i * 60);
    });
  };

  function prepareRevealPatterns() {
    applyRevealPattern($('.hero'), 'editorial');
    $$('[data-spoton-menu] [data-spoton-grid]').forEach((grid) => applyRevealPattern(grid, 'order-reverse'));
    $$('.bleed-grid').forEach((grid) => applyRevealPattern(grid, 'tiles-after-hero'));
    $$('.instagram-grid, .provisions-rail, .gallery, [data-lightbox-gallery]').forEach((grid) => applyRevealPattern(grid, 'gallery'));
    $$('.venue-card-grid, .grid, .catering-mini-grid, .catering-package-grid, .catering-sample-grid, .events, .meta-events__grid, .meta-press__grid, .jobs-grid').forEach((grid) => applyRevealPattern(grid, 'cards'));
    $$('.text-image, .featured-event, .meta-detail__layout, .catering-form, .contact-form').forEach((section) => applyRevealPattern(section, 'editorial'));
  }

  function initBleedGridImages() {
    $$('.bleed-block').forEach((block) => {
      const bg = $('.bleed-block__bg', block);
      if (!bg) {
        block.classList.add('is-bg-loaded');
        return;
      }

      const bgImage = window.getComputedStyle(bg).backgroundImage || '';
      const match = bgImage.match(/url\(["']?([^"')]+)["']?\)/);
      if (!match || !match[1]) {
        block.classList.add('is-bg-loaded');
        return;
      }

      const img = new Image();
      img.onload = () => block.classList.add('is-bg-loaded');
      img.onerror = () => block.classList.add('is-bg-loaded');
      img.src = match[1];
      if (img.complete) block.classList.add('is-bg-loaded');
    });
  }

  /* ---- Theme toggle (light/dark) ---- */
  function initThemeToggle() {
    const root = document.documentElement;
    const btns = $$('[data-theme-toggle]');
    if (!btns.length) return;
    const set = (mode) => {
      root.setAttribute('data-theme', mode);
      try { localStorage.setItem('lb-theme', mode); } catch (e) {}
      btns.forEach(b => b.setAttribute('aria-pressed', mode === 'dark'));
    };
    btns.forEach(b => b.addEventListener('click', () => {
      const cur = root.getAttribute('data-theme');
      set(cur === 'dark' ? 'light' : 'dark');
    }));
    btns.forEach(b => b.setAttribute('aria-pressed', root.getAttribute('data-theme') === 'dark'));
  }

  /* ---- Mobile nav + dropdown triggers ---- */
  function initNav() {
    const toggle = $('.nav-toggle');
    const nav = $('.nav');
    const setNavOpen = (open) => {
      nav.classList.toggle('is-open', open);
      toggle.setAttribute('aria-expanded', String(open));
      toggle.setAttribute('aria-label', open ? (toggle.dataset.labelClose || 'Close menu') : (toggle.dataset.labelOpen || 'Open menu'));
      if (!open) $$('.nav__trigger').forEach((t) => t.setAttribute('aria-expanded', 'false'));
    };
    if (toggle && nav) {
      toggle.addEventListener('click', () => setNavOpen(!nav.classList.contains('is-open')));
      // Following a link closes the drawer (matters for same-page #anchors).
      nav.addEventListener('click', (e) => { if (e.target.closest('a[href]')) setNavOpen(false); });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && nav.classList.contains('is-open') && !$('.nav__trigger[aria-expanded="true"]')) {
          setNavOpen(false);
          toggle.focus();
        }
      });
    }

    // Dropdown triggers: click toggles on touch/mobile; hover/focus handles desktop via CSS.
    $$('.nav__trigger').forEach((trigger) => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        const expanded = trigger.getAttribute('aria-expanded') === 'true';
        // Close sibling dropdowns
        $$('.nav__trigger').forEach((t) => { if (t !== trigger) t.setAttribute('aria-expanded', 'false'); });
        trigger.setAttribute('aria-expanded', String(!expanded));
      });
    });

    // Close any open dropdown when clicking outside the nav
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.nav__item')) {
        $$('.nav__trigger').forEach((t) => t.setAttribute('aria-expanded', 'false'));
      }
    });

    // Escape closes dropdowns and returns focus to the trigger
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        $$('.nav__trigger[aria-expanded="true"]').forEach((t) => {
          t.setAttribute('aria-expanded', 'false');
          t.focus();
        });
      }
    });
  }

  /* ---- Scroll reveal ---- */
  function initReveal() {
    if (prefersReduced || !T.anim || !T.anim.enabled) {
      $$('[data-reveal]').forEach(el => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          io.unobserve(e.target);
          // Elements already in view on load can intersect before the browser
          // has painted their initial (hidden) state even once, so adding
          // is-visible synchronously here sometimes skips the CSS transition
          // entirely and leaves them stuck at opacity:0. Deferring by two
          // frames guarantees at least one paint of the hidden state first.
          requestAnimationFrame(() => requestAnimationFrame(() => {
            e.target.classList.add('is-visible');
          }));
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -10% 0px' });
    $$('[data-reveal]').forEach(el => io.observe(el));

    // Safety net: content must never be permanently stuck invisible (e.g. on
    // a purchase page) if the observer misses an element for any reason.
    // Scoped to elements actually on screen right now — a blanket "reveal
    // everything not yet visible" would force-show below-the-fold sections
    // before the user ever scrolls to them, killing their scroll-reveal
    // animation entirely (they'd already be marked is-visible by the time
    // the user got there).
    window.setTimeout(() => {
      $$('[data-reveal]:not(.is-visible)').forEach(el => {
        const r = el.getBoundingClientRect();
        if (r.top < window.innerHeight && r.bottom > 0) el.classList.add('is-visible');
      });
    }, 2500);
  }

  /* ---- Hero cross-fade ---- */
  function initHero() {
    $$('[data-hero-crossfade]').forEach((hero) => {
      const slides = $$('.hero__slide', hero);
      const contents = $$('.hero__content', hero);
      const dots = $$('[data-hero-dot]', hero);
      if (slides.length < 2) return;
      let idx = 0;
      let timer = null;

      const showSlide = (nextIdx) => {
        if (nextIdx === idx) return;
        slides[idx].classList.remove('is-active');
        contents[idx] && contents[idx].classList.remove('is-active');
        dots[idx] && dots[idx].classList.remove('is-active');
        dots[idx] && dots[idx].setAttribute('aria-pressed', 'false');
        idx = nextIdx;
        slides[idx].classList.add('is-active');
        contents[idx] && contents[idx].classList.add('is-active');
        dots[idx] && dots[idx].classList.add('is-active');
        dots[idx] && dots[idx].setAttribute('aria-pressed', 'true');
      };

      const interval = (T.anim && T.anim.heroInterval ? T.anim.heroInterval : 6) * 1000;
      const startTimer = () => {
        if (!T.anim || !T.anim.heroCrossfade || prefersReduced) return;
        window.clearInterval(timer);
        timer = window.setInterval(() => {
          showSlide((idx + 1) % slides.length);
        }, interval);
      };

      slides[0].classList.add('is-active');
      contents[0] && contents[0].classList.add('is-active');
      dots[0] && dots[0].classList.add('is-active');
      dots[0] && dots[0].setAttribute('aria-pressed', 'true');

      dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const nextIdx = parseInt(dot.dataset.heroDot || '0', 10);
          showSlide(nextIdx);
          startTimer();
        });
      });

      startTimer();
    });
  }

  /* ---- SpotOn menu previews ---- */
  async function initSpotonMenus() {
    const sections = $$('[data-spoton-menu]');
    if (!sections.length || !T.formsApiBase) return;

    await Promise.all(sections.map(async (section) => {
      const grid = $('[data-spoton-grid]', section);
      if (!grid) return;
      const limit = parseInt(section.dataset.limit || '4', 10);
      const orderUrl = section.dataset.orderUrl || '#';
      try {
        const url = new URL(`${T.formsApiBase}/api/spoton/menu-items`);
        url.searchParams.set('limit', String(limit));
        const res = await fetch(url.toString(), { headers: { ...(T.formsOriginToken ? { 'X-LB-Token': T.formsOriginToken } : {}) } });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.ok || !Array.isArray(data.items) || data.items.length === 0) {
          grid.dataset.placeholderActive = 'true';
          return;
        }

        grid.innerHTML = data.items.slice(0, limit).map((item, itemIndex) => {
          const price = item.price ? `$${Number(item.price).toFixed(2)}` : '';
          const href = item.orderUrl || orderUrl;
          const name = escapeHtml(item.name);
          const description = escapeHtml(item.description);
          return `
            <article class="card order-card" data-reveal>
              ${item.imageUrl ? `<div class="card__media"><img src="${escapeHtml(item.imageUrl)}" alt="${name}" loading="lazy"></div>` : `<div class="card__media order-card__placeholder order-card__placeholder--${(itemIndex % 4) + 1}"></div>`}
              <h3 class="h5">${name}</h3>
              ${description ? `<p class="muted">${description}</p>` : ''}
              ${price ? `<p class="order-card__price">${price}</p>` : ''}
              <a class="btn btn--secondary btn--block" href="${escapeHtml(href)}" target="_blank" rel="noopener">Order Online</a>
            </article>`;
        }).join('');
        $$('[data-reveal]', grid).forEach((el) => { delete el.dataset.revealPrepared; });
        applyRevealPattern(grid, 'order-reverse');
        revealNow(grid);
      } catch (e) {
        // Keep editor/fallback cards in place if SpotOn is not configured yet.
      }
    }));
  }

  /* ---- Instagram feed ---- */
  /* Instagram feed: shows a quiet skeleton, preloads every photo, drops any
     that fail, then fades the whole grid in at once (no one-by-one pop-in).
     The last good feed is cached so repeat visits render instantly while the
     fresh one loads. */
  const IG_CACHE_KEY = 'lb-instagram-feed-v2';

  function preloadImage(src, timeoutMs) {
    return new Promise((resolve) => {
      const img = new Image();
      let done = false;
      const finish = (ok) => { if (!done) { done = true; resolve(ok); } };
      // Loaded with real dimensions = ready. (img.decode() can stall
      // indefinitely, e.g. in background tabs, so it isn't used.)
      img.onload = () => finish(img.naturalWidth > 0);
      img.onerror = () => finish(false);
      setTimeout(() => finish(img.naturalWidth > 0), timeoutMs);
      img.src = src;
    });
  }

  function renderInstagramGrid(grid, items) {
    grid.innerHTML = items.map((item) => `
      <a class="instagram-card instagram-card--api" href="${escapeHtml(item.permalink)}" target="_blank" rel="noopener">
        <span class="visually-hidden">Open this Instagram post</span>
        <img src="${escapeHtml(item.imageUrl)}" alt="${escapeHtml((item.caption || 'Lazy Butcher Instagram post').slice(0, 140))}" decoding="async">
      </a>
    `).join('');
    grid.dataset.state = 'ready';
    grid.removeAttribute('aria-busy');
  }

  async function initInstagramFeeds() {
    const sections = $$('[data-instagram-feed]');
    if (!sections.length || !T.formsApiBase) return;

    await Promise.all(sections.map(async (section) => {
      const grid = $('[data-instagram-grid]', section);
      if (!grid) return;
      const limit = parseInt(section.dataset.limit || '6', 10);
      const fallBack = () => { grid.dataset.state = 'fallback'; grid.dataset.placeholderActive = 'true'; grid.removeAttribute('aria-busy'); };
      // Never leave the shimmer up if the API is slow or unreachable.
      setTimeout(() => { if (grid.dataset.state === 'loading') fallBack(); }, 8000);

      // 1) Last good feed, straight from cache (those images are usually in
      //    the browser cache too, so they appear immediately).
      let cached = null;
      try { cached = JSON.parse(localStorage.getItem(IG_CACHE_KEY) || 'null'); } catch (e) { /* storage blocked */ }
      if (cached && Array.isArray(cached.items) && cached.items.length) {
        const ok = await Promise.all(cached.items.slice(0, limit).map((i) => preloadImage(i.imageUrl, 1500)));
        const good = cached.items.slice(0, limit).filter((_, n) => ok[n]);
        if (good.length >= Math.min(limit, 4)) renderInstagramGrid(grid, good);
      }

      // 2) Fresh feed.
      try {
        const url = new URL(`${T.formsApiBase}/api/instagram/media`);
        url.searchParams.set('limit', String(limit));
        const res = await fetch(url.toString(), { headers: { ...(T.formsOriginToken ? { 'X-LB-Token': T.formsOriginToken } : {}) } });
        const data = await res.json().catch(() => ({}));
        const items = (data && data.ok && Array.isArray(data.items) ? data.items : [])
          .map((i) => Object.assign({}, i, { imageUrl: i.imageUrl || (i.mediaType === 'VIDEO' ? i.thumbnailUrl : i.mediaUrl || i.thumbnailUrl) }))
          .filter((i) => i.imageUrl);
        if (!items.length) { if (grid.dataset.state !== 'ready') fallBack(); return; }

        const ok = await Promise.all(items.map((i) => preloadImage(i.imageUrl, 6000)));
        const good = items.filter((_, n) => ok[n]).slice(0, limit);
        if (!good.length) { if (grid.dataset.state !== 'ready') fallBack(); return; }

        const sameAsShown = grid.dataset.state === 'ready' &&
          $$('img', grid).map((img) => img.getAttribute('src')).join('|') === good.map((i) => i.imageUrl).join('|');
        if (!sameAsShown) renderInstagramGrid(grid, good);
        try { localStorage.setItem(IG_CACHE_KEY, JSON.stringify({ at: Date.now(), items: good })); } catch (e) { /* storage full/blocked */ }
      } catch (e) {
        if (grid.dataset.state !== 'ready') fallBack();
      }
    }));
  }

  /* ---- Product recommendations ("people also bought") ---- */
  async function initProductRecommendations() {
    const wrappers = $$('[data-product-recommendations]');
    if (!wrappers.length) return;

    await Promise.all(wrappers.map(async (wrapper) => {
      const url = wrapper.dataset.url;
      if (!url) return;
      try {
        const res = await fetch(url);
        if (!res.ok) return;
        const text = await res.text();
        const doc = document.createElement('div');
        doc.innerHTML = text;
        const rendered = doc.querySelector('[data-product-recommendations]');
        if (!rendered || !rendered.innerHTML.trim()) return;

        wrapper.innerHTML = rendered.innerHTML;
        const grid = $('[data-reveal-group]', wrapper);
        if (grid) {
          $$('[data-reveal]', grid).forEach((el) => { delete el.dataset.revealPrepared; });
          applyRevealPattern(grid, 'gallery');
          revealNow(grid);
        }
      } catch (e) {
        // Leave the section empty/hidden if recommendations can't be fetched.
      }
    }));
  }

  /* ---- Popups (onload promo + exit-intent) ----
     Two independent popups, each with its own dismiss memory, but only one
     shows per page load — whichever fires first wins and blocks the other. */
  let anyPopupOpenedThisLoad = false;

  function setupPopup(popup, dismissKey) {
    const close = $('[data-popup-close]', popup);
    const expiryDays = parseInt(popup.dataset.expiryDays || '3', 10);
    const expiryMs = expiryDays * 24 * 60 * 60 * 1000;
    const lastDismiss = (() => {
      try { return parseInt(localStorage.getItem(dismissKey) || '0', 10); } catch (e) { return 0; }
    })();
    if (lastDismiss && (Date.now() - lastDismiss < expiryMs)) return null;

    const popupName = dismissKey.replace('lb-', '').replace('-dismissed-at', '');
    let opened = false;
    const open = () => {
      if (opened || anyPopupOpenedThisLoad) return;
      opened = true;
      anyPopupOpenedThisLoad = true;
      document.documentElement.classList.add('has-modal-open');
      popup.classList.add('is-open');
      const firstFocusable = popup.querySelector('input, button, a, [tabindex]');
      if (firstFocusable && !window.matchMedia('(pointer: coarse)').matches) {
        firstFocusable.focus({ preventScroll: true });
      }
      trackEvent('popup_shown', { popup_name: popupName, campaign_name: popup.dataset.promoCampaign || '' });
    };
    const dismiss = () => {
      if (!opened) return;
      popup.classList.remove('is-open');
      document.documentElement.classList.remove('has-modal-open');
      try { localStorage.setItem(dismissKey, String(Date.now())); } catch (e) {}
      trackEvent('popup_dismissed', { popup_name: popupName });
      popup.dispatchEvent(new CustomEvent('lb:popup-dismissed', { bubbles: true, detail: { popupName } }));
    };
    close && close.addEventListener('click', dismiss);
    $$('[data-popup-dismiss]', popup).forEach((link) => link.addEventListener('click', dismiss));
    popup.addEventListener('click', (e) => { if (e.target === popup) dismiss(); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && popup.classList.contains('is-open')) dismiss(); });
    popup.addEventListener('lb:form-success', dismiss);
    return { open, isOpen: () => opened };
  }

  /* Onload promo popup — delay + scroll-depth trigger. */
  function initOnloadPopup() {
    const popup = $('[data-onload-popup]');
    if (!popup) return;
    const controller = setupPopup(popup, popup.dataset.dismissKey || 'lb-onload-popup-dismissed-at');
    if (!controller) return;

    const delay = parseInt(popup.dataset.delay || '4000', 10);
    const scrollThreshold = () => Math.max(120, Math.min(300, window.innerHeight * 0.3));
    const onScroll = () => {
      if (window.scrollY >= scrollThreshold()) {
        controller.open();
        window.removeEventListener('scroll', onScroll);
      }
    };
    setTimeout(() => {
      controller.open();
      window.removeEventListener('scroll', onScroll);
    }, delay);
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* Exit-intent popup — fires once the cursor leaves toward the top of the
     viewport, after a minimum time on page so it can't fire on page load. */
  function initExitIntentPopup() {
    const popup = $('[data-exit-intent-popup]');
    if (!popup) return;
    const controller = setupPopup(popup, 'lb-exit-intent-popup-dismissed-at');
    if (!controller) return;

    const minTimeMs = parseInt(popup.dataset.minTime || '8000', 10);
    let minTimePassed = false;
    setTimeout(() => { minTimePassed = true; }, minTimeMs);
    const onMouseLeave = (e) => {
      if (!minTimePassed || e.clientY >= 5) return;
      // Someone mid-way through a form gets the recovery popup instead.
      if (document.documentElement.dataset.formStarted) return;
      controller.open();
      document.removeEventListener('mouseleave', onMouseLeave);
    };
    document.addEventListener('mouseleave', onMouseLeave);
  }

  /* ---- Forms (newsletter, catering, contact) ---- */
  async function postForm(endpoint, payload) {
    if (!T.formsApiBase) throw new Error('Forms API base URL is not configured in theme settings.');
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 20000);
    try {
      const res = await fetch(`${T.formsApiBase}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(T.formsOriginToken ? { 'X-LB-Token': T.formsOriginToken } : {}),
        },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) throw new Error(data.error || 'Submission failed. Please try again.');
      return data;
    } catch (error) {
      if (error && error.name === 'AbortError') {
        throw new Error('The request timed out. Please try again.');
      }
      throw error;
    } finally {
      clearTimeout(timeout);
    }
  }

  function waitForTurnstile() {
    if (!T.turnstileSiteKey) return Promise.resolve(null);
    if (window.turnstile) return Promise.resolve(window.turnstile);
    return new Promise((resolve, reject) => {
      const started = Date.now();
      const timer = window.setInterval(() => {
        if (window.turnstile) {
          window.clearInterval(timer);
          resolve(window.turnstile);
        } else if (Date.now() - started > 8000) {
          window.clearInterval(timer);
          reject(new Error('Security verification did not load. Please refresh and try again.'));
        }
      }, 50);
    });
  }

  async function createTurnstileToken(formEl, endpoint) {
    const turnstile = await waitForTurnstile();
    if (!turnstile) return '';

    let slot = $('[data-turnstile-slot]', formEl);
    if (!slot) {
      slot = document.createElement('div');
      slot.dataset.turnstileSlot = '';
      slot.style.gridColumn = '1 / -1';
      const status = $('[data-form-status]', formEl);
      formEl.insertBefore(slot, status || null);
    }

    return new Promise((resolve, reject) => {
      let widgetId;
      let settled = false;
      const cleanup = () => {
        if (widgetId !== undefined) {
          try { turnstile.remove(widgetId); } catch (e) { /* best effort */ }
        }
        slot.replaceChildren();
      };
      const finish = (error, token) => {
        if (settled) return;
        settled = true;
        cleanup();
        if (error) reject(error);
        else resolve(token);
      };

      try {
        const action = (endpoint || '').split('/').pop() || 'form';
        widgetId = turnstile.render(slot, {
          sitekey: T.turnstileSiteKey,
          execution: 'execute',
          appearance: 'interaction-only',
          action,
          callback: (token) => finish(null, token),
          'error-callback': () => finish(new Error('Security verification failed. Please try again.')),
          'expired-callback': () => finish(new Error('Security verification expired. Please try again.')),
          'timeout-callback': () => finish(new Error('Security verification timed out. Please try again.')),
        });
        turnstile.execute(widgetId);
      } catch (error) {
        finish(error instanceof Error ? error : new Error('Security verification failed. Please try again.'));
      }
    });
  }

  /* ---- GTM / GA4 event tracking ----
     Pushes to window.dataLayer, which is safe to call even if GTM hasn't
     loaded yet (GTM drains the array once it initializes) or isn't present
     at all (dataLayer is just a plain array in that case, harmless). */
  function trackEvent(name, params) {
    try {
      window.dataLayer = window.dataLayer || [];
      const data = Object.assign({}, params || {});
      // GA4 ecommerce events read items/value/currency from an `ecommerce`
      // object, and the previous one must be cleared first or GTM merges them.
      if (Array.isArray(data.items)) {
        const ecommerce = { items: data.items };
        if (data.currency !== undefined) ecommerce.currency = data.currency;
        if (data.value !== undefined) ecommerce.value = data.value;
        delete data.items; delete data.currency; delete data.value;
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push(Object.assign({ event: name, ecommerce }, data));
        return;
      }
      window.dataLayer.push(Object.assign({ event: name }, data));
    } catch (e) { /* analytics should never break the page */ }
  }
  window.lbTrackEvent = trackEvent;

  function bindForm(formEl) {
    const endpoint = formEl.dataset.endpoint;
    const statusEl = $('[data-form-status]', formEl);
    const submitBtn = $('button[type="submit"]', formEl);
    formEl.addEventListener('submit', async (e) => {
      e.preventDefault();
      const honeypot = formEl.querySelector('[name="lb_hp"]');
      if (honeypot && honeypot.value) return; // bot
      if (submitBtn) submitBtn.disabled = true;
      if (statusEl) { statusEl.textContent = ''; statusEl.className = 'field__hint'; }
      const fd = new FormData(formEl);
      const payload = {};
      fd.forEach((v, k) => {
        if (payload[k] !== undefined) {
          payload[k] = [].concat(payload[k], v);
        } else {
          payload[k] = v;
        }
      });
      payload.source = window.location.pathname;
      if (formEl.dataset.formName) payload.form_name = formEl.dataset.formName;
      try {
        const turnstileToken = await createTurnstileToken(formEl, endpoint);
        if (turnstileToken) payload.turnstile_token = turnstileToken;
        const result = await postForm(endpoint, payload);
        if (statusEl) {
          // Past unsubscribers must confirm by email before they're re-added.
          statusEl.textContent = result && result.confirmEmail
            ? 'Almost there: check your inbox for a confirmation email to finish signing up.'
            : formEl.dataset.successMessage || 'Thanks!';
          statusEl.className = 'alert alert--success';
        }
        formEl.reset();
        // Bubble a custom event so the popup (or other listeners) can react
        formEl.dispatchEvent(new CustomEvent('lb:form-success', { bubbles: true, detail: { endpoint } }));
        // Every lead-gen form on the site (newsletter in all its placements,
        // catering, contact, the shop waitlist, and anything future) funnels
        // through this one handler, so this single push covers all of them.
        const leadType = formEl.dataset.leadType || (endpoint || '').split('/').pop() || 'form';
        const formName = formEl.dataset.formName || formEl.dataset.recoveryName || formEl.id || leadType;
        trackEvent('generate_lead', {
          lead_type: leadType,
          form_name: formName,
          form_endpoint: endpoint,
          form_tags: typeof payload.tags === 'string' ? payload.tags : '',
          page_path: window.location.pathname
        });
        if (leadType === 'newsletter') trackEvent('sign_up', { method: 'newsletter', form_name: formName });
        // Dedicated events so GA4/Ads can count these forms as separate conversions.
        if (leadType === 'contact') trackEvent('contact_form_submit', { form_name: formName });
        if (leadType === 'catering') trackEvent('catering_form_submit', { form_name: formName });
        if (leadType === 'store_waitlist') trackEvent('store_waitlist_signup', { form_name: formName });
      } catch (err) {
        const errorMessage = String(err && err.message || 'Submission failed. Please try again.');
        trackEvent('form_error', { form_name: formEl.dataset.formName || endpoint, form_endpoint: endpoint, error_message: errorMessage.slice(0, 100) });
        if (statusEl) {
          statusEl.textContent = formEl.dataset.errorMessage || errorMessage;
          statusEl.className = 'alert alert--error';
        }
      } finally {
        if (submitBtn) submitBtn.disabled = false;
      }
    });
  }
  function initForms() {
    $$('form[data-lb-form]').forEach(bindForm);
  }

  /* ---- Abandoned-form recovery ----
     Forms marked data-recovery="catering|contact" count as "started" once
     someone types in them. If they then leave (exit intent on desktop, or
     switching away and back on mobile), or close the form's modal, without
     sending, offer a two-field email + message popup routed to the same
     inbox. Once per session; no marketing opt-ins. */
  const RECOVERY_SHOWN_KEY = 'lb-form-recovery-shown';
  let recoveryForm = null;

  function formHasProgress(form) {
    const menuPicks = form.querySelector('[data-builder-selections]');
    if (menuPicks && menuPicks.value && !menuPicks.value.startsWith('Format: Not sure yet')) return true;
    return Array.from(form.elements).some((el) => {
      if (!el.name || el.name === 'lb_hp' || el.type === 'hidden') return false;
      if (el.type === 'checkbox' || el.type === 'radio' || el.tagName === 'SELECT') return false;
      return el.value && el.value.trim().length > 0;
    });
  }

  function recoveryContext(form) {
    const ctx = {};
    Array.from(form.elements).forEach((el) => {
      if (!el.name || el.name === 'lb_hp' || el.name === 'email' || el.name === 'tags' || el.name.startsWith('builder-')) return;
      if (el.type === 'hidden' || el.type === 'submit') return;
      if ((el.type === 'checkbox' || el.type === 'radio') && !el.checked) return;
      const v = (el.value || '').trim();
      if (!v) return;
      ctx[el.name] = ctx[el.name] ? `${ctx[el.name]}, ${v}` : v;
    });
    const menuPicks = form.querySelector('[data-builder-selections]');
    if (menuPicks && menuPicks.value) ctx.menu_selections = menuPicks.value;
    const estimate = form.querySelector('[data-builder-estimate]');
    if (estimate && estimate.value) ctx.estimate = estimate.value;
    return ctx;
  }

  function requestFormRecovery(form) {
    const modal = $('[data-form-recovery]');
    const target = form || recoveryForm;
    if (!modal || !target || target.__lbSubmitted || !formHasProgress(target)) return;
    try { if (sessionStorage.getItem(RECOVERY_SHOWN_KEY)) return; } catch (e) { /* storage blocked */ }
    if (!modal.hidden) return;

    const dept = target.dataset.recovery === 'catering' ? 'catering' : 'contact';
    const email = modal.dataset[`${dept}Email`];
    const phone = modal.dataset[`${dept}Phone`];
    $('[data-recovery-dept]', modal).value = dept;
    $('[data-recovery-started]', modal).value = target.dataset.recoveryName || document.title;
    $('[data-recovery-context]', modal).value = JSON.stringify(recoveryContext(target));
    $('[data-recovery-eyebrow]', modal).textContent = dept === 'catering' ? 'Catering' : 'Before you go';
    const typedEmail = target.querySelector('[name="email"]');
    const emailInput = $('#RecoveryEmail', modal);
    if (typedEmail && typedEmail.value && emailInput && !emailInput.value) emailInput.value = typedEmail.value;
    const emailLink = $('[data-recovery-email-link]', modal);
    const phoneLink = $('[data-recovery-phone-link]', modal);
    if (emailLink && email) { emailLink.href = `mailto:${email}`; emailLink.textContent = email; }
    if (phoneLink && phone) { phoneLink.href = `tel:${phone.replace(/[^0-9+]/g, '')}`; phoneLink.textContent = phone; }

    try { sessionStorage.setItem(RECOVERY_SHOWN_KEY, '1'); } catch (e) { /* storage blocked */ }
    openModal(modal);
    trackEvent('form_recovery_shown', { recovery_dept: dept, started_form: target.dataset.recoveryName || '' });
  }

  function initFormRecovery() {
    const modal = $('[data-form-recovery]');
    if (!modal) return;
    wireModalDismiss(modal);

    $$('form[data-recovery]').forEach((form) => {
      form.addEventListener('input', () => {
        if (form.__lbSubmitted) return;
        recoveryForm = form;
        document.documentElement.dataset.formStarted = '1';
      });
      form.addEventListener('lb:form-success', () => {
        form.__lbSubmitted = true;
        if (recoveryForm === form) recoveryForm = null;
        delete document.documentElement.dataset.formStarted;
      });
    });

    const recoveryFormEl = $('form', modal);
    if (recoveryFormEl) {
      recoveryFormEl.addEventListener('lb:form-success', () => {
        if (recoveryForm) recoveryForm.__lbSubmitted = true;
        recoveryForm = null;
        delete document.documentElement.dataset.formStarted;
        setTimeout(() => closeModal(modal), 2500);
      });
    }

    // Desktop: cursor leaves toward the browser chrome.
    document.addEventListener('mouseleave', (e) => {
      if (e.clientY < 5) requestFormRecovery();
    });
    // Mobile: they switched apps/tabs mid-form and came back.
    let wasHidden = false;
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') { wasHidden = true; return; }
      if (wasHidden) { wasHidden = false; requestFormRecovery(); }
    });
  }

  /* ---- Catering menu builder (inside the build-your-event modal) ----
     Renders the real catering menu (snippets/catering-menu-data.liquid) as
     choices, keeps a live estimate, and writes a readable summary into
     hidden fields (package / selections / estimate) that post with the
     inquiry. Builder inputs have no name attribute on purpose, so only the
     summary is sent. */
  function initCateringBuilder(modal) {
    const root = $('[data-builder-root]', modal);
    const dataEl = $('[data-catering-menu]', modal.parentElement) || $('[data-catering-menu]');
    if (!root || !dataEl) return;
    let menu;
    try { menu = JSON.parse(dataEl.textContent); } catch (e) { return; }
    const form = $('form', modal);
    const guestsInput = form.querySelector('[name="guests"]');
    const dateInput = form.querySelector('[name="event_date"]');
    const telLink = $('a[href^="tel:"]', modal);
    const callLink = telLink ? `<a href="${escapeHtml(telLink.getAttribute('href'))}">${escapeHtml(telLink.textContent.trim())}</a>` : 'us';
    // Weekdays between today and the event (the menu is due 7 business days out).
    const businessDaysUntil = (value) => {
      const event = value ? new Date(`${value}T00:00:00`) : null;
      if (!event || Number.isNaN(event.getTime())) return null;
      const day = new Date(); day.setHours(0, 0, 0, 0);
      let count = 0;
      for (day.setDate(day.getDate() + 1); day < event && count < 8; day.setDate(day.getDate() + 1)) {
        if (day.getDay() % 6) count += 1;
      }
      return count;
    };
    const uid = modal.id;
    const money = (n) => `$${n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
    const esc = escapeHtml;
    const checks = (group, items, max) => items.map((label, i) => `
      <label class="checkbox event-builder__chip"><input type="checkbox" data-group="${group}" value="${esc(label)}"${max ? ` data-max="${max}"` : ''}> <span>${esc(label)}</span></label>`).join('');
    const qty = (group, i, label, price, unit, perPerson) => `
      <label class="event-builder__qty">
        <span>${esc(label)} <small class="muted">${money(price)}${unit ? ` / ${esc(unit)}` : ''}</small></span>
        <span class="event-builder__qty-ctl">
          ${perPerson ? `<button type="button" class="event-builder__fill" data-fill="${group}-${i}">All guests</button>` : ''}
          <input class="input" type="number" min="0" step="1" value="0" inputmode="numeric" data-qty="${group}" data-index="${i}" data-fill-id="${group}-${i}" aria-label="${esc(label)} quantity${perPerson ? ' (number of guests)' : ''}">
        </span>
      </label>`;

    root.innerHTML = `
      <fieldset class="event-builder__step">
        <legend class="label">1. Pick a format</legend>
        <div class="event-builder__formats">
          ${[['buffet', menu.buffet.label, `${money(menu.buffet.price)} / person`],
             ['plates', 'Plates', `from ${money(menu.plates.tiers[0].price)} / person`],
             ['boxed', 'Boxed lunches', `from ${money(menu.boxed.tiers[0].price)} / person`],
             ['boxes', 'Feast & Slider Boxes', `from ${money(Math.min(...menu.boxes.map((b) => b.price)))}`],
             ['alacarte', 'À la carte', 'meats by the pound'],
             ['unsure', 'Not sure yet', "we'll help you choose"]].map(([id, label, sub]) => `
            <label class="event-builder__format">
              <input type="radio" name="builder-format-${uid}" value="${id}"${id === 'unsure' ? ' checked' : ''} data-format>
              <span><strong>${esc(label)}</strong><small>${sub}</small></span>
            </label>`).join('')}
        </div>
      </fieldset>

      <div class="event-builder__panel" data-panel="buffet" hidden>
        <p class="event-builder__note">${esc(menu.buffet.desc)} ${money(menu.buffet.price)} per person.</p>
        <p class="label">Meats <small class="muted" data-limit-note="buffet-meats"></small></p>
        <div class="event-builder__chips">${checks('buffet-meats', menu.meats)}</div>
        <p class="label">Sides <small class="muted" data-limit-note="buffet-sides"></small></p>
        <div class="event-builder__chips">${checks('buffet-sides', menu.sides)}</div>
      </div>

      <div class="event-builder__panel" data-panel="plates" hidden>
        <p class="event-builder__note">Per person, minimum ${menu.plates.min} guests.</p>
        <div class="field">
          <label class="label" for="${uid}-plate-tier">Plate</label>
          <select class="select" id="${uid}-plate-tier" data-tier="plates">
            ${menu.plates.tiers.map((t, i) => `<option value="${i}">${esc(t.label)} (${money(t.price)} / person)</option>`).join('')}
          </select>
        </div>
        <p class="label">Meats <small class="muted" data-limit-note="plate-meats"></small></p>
        <div class="event-builder__chips">${checks('plate-meats', menu.meats)}</div>
        <p class="label">Sides <small class="muted" data-limit-note="plate-sides"></small></p>
        <div class="event-builder__chips">${checks('plate-sides', menu.sides)}</div>
      </div>

      <div class="event-builder__panel" data-panel="boxed" hidden>
        <p class="event-builder__note">Per person, minimum ${menu.boxed.min} guests.</p>
        <div class="field">
          <label class="label" for="${uid}-boxed-tier">Boxed lunch</label>
          <select class="select" id="${uid}-boxed-tier" data-tier="boxed">
            ${menu.boxed.tiers.map((t, i) => `<option value="${i}">${esc(t.label)} (${money(t.price)} / person)</option>`).join('')}
          </select>
        </div>
        <p class="label">Sandwiches</p>
        <div class="event-builder__chips">${checks('boxed-sandwiches', menu.boxed.sandwiches)}</div>
        <div data-boxed-sides>
          <p class="label">Sides <small class="muted" data-limit-note="boxed-sides"></small></p>
          <div class="event-builder__chips">${checks('boxed-sides', menu.sides)}</div>
        </div>
      </div>

      <div class="event-builder__panel" data-panel="boxes" hidden>
        ${menu.boxes.map((b, i) => `
          <div class="event-builder__box">
            ${qty('boxes', i, b.label, b.price, 'box')}
            <p class="muted event-builder__desc">${esc(b.desc)}</p>
          </div>`).join('')}
        <p class="label">Meats for your boxes</p>
        <div class="event-builder__chips">${checks('box-meats', menu.meats)}</div>
        <p class="label">Sides for Feast Boxes <small class="muted">(2 quarts per box)</small></p>
        <div class="event-builder__chips">${checks('box-sides', menu.sides)}</div>
      </div>

      <div class="event-builder__panel" data-panel="alacarte" hidden>
        <p class="label">Meats by the pound</p>
        <div class="event-builder__qtys">${menu.alacarte.map((m, i) => qty('alacarte', i, m.label, m.price, m.unit)).join('')}</div>
        <p class="event-builder__note">Add sides by the full pan under add-ons below.</p>
      </div>

      <fieldset class="event-builder__step">
        <legend class="label">2. Add-ons <small class="muted">(optional, enter quantities)</small></legend>
        <p class="label">Ribs & wings</p>
        <div class="event-builder__qtys">${menu.addonMeats.map((m, i) => qty('addonMeats', i, m.label, m.price, m.unit)).join('')}</div>
        <p class="label">Extra sides <small class="muted">${money(menu.sidePan.price)} per ${esc(menu.sidePan.unit)}</small></p>
        <div class="event-builder__qtys">${menu.sides.map((side, i) => qty('sidepans', i, side, menu.sidePan.price, menu.sidePan.unit)).join('')}</div>
        <p class="label">Trays, salad & cornbread <small class="muted">per person; trays minimum ${menu.trays.min}</small></p>
        <div class="event-builder__qtys">${menu.trays.items.map((t, i) => qty('trays', i, t.label, t.price, 'person', true)).join('')}</div>
        <p class="label">Desserts</p>
        <div class="event-builder__qtys">${menu.desserts.map((d, i) => qty('desserts', i, d.label, d.price, d.unit)).join('')}</div>
        <p class="label">Beverages <small class="muted">per person</small></p>
        <div class="event-builder__qtys">${menu.drinks.map((d, i) => qty('drinks', i, d.label, d.price, d.unit, true)).join('')}</div>
      </fieldset>
    `;

    const panel = $('[data-builder-estimate-panel]', modal);
    const pkgInput = $('[data-builder-package]', form);
    const selInput = $('[data-builder-selections]', form);
    const estInput = $('[data-builder-estimate]', form);

    const checked = (group) => $$(`input[data-group="${group}"]:checked`, root).map((el) => el.value);
    const qtys = (group, list, labelOf) => $$(`input[data-qty="${group}"]`, root)
      .map((el) => ({ n: Math.max(0, parseInt(el.value || '0', 10) || 0), item: list[parseInt(el.dataset.index, 10)] }))
      .filter((x) => x.n > 0)
      .map((x) => ({ ...x, label: labelOf ? labelOf(x.item) : x.item.label }));

    function enforceLimit(group, max, upTo) {
      const boxes = $$(`input[data-group="${group}"]`, root);
      const count = boxes.filter((b) => b.checked).length;
      boxes.forEach((b) => { b.disabled = max > 0 && !b.checked && count >= max; });
      const note = $(`[data-limit-note="${group}"]`, root);
      if (note) note.textContent = max ? `choose ${upTo ? 'up to ' : ''}${max} (${count}/${max})` : '';
    }

    function update() {
      const format = ($('input[data-format]:checked', root) || {}).value || 'unsure';
      $$('[data-panel]', root).forEach((p) => { p.hidden = p.dataset.panel !== format; });
      const guests = Math.max(0, parseInt((guestsInput && guestsInput.value) || '0', 10) || 0);
      const lines = []; // [label, amount|null]
      const summary = [];
      const warnings = [];
      let pkg = 'Not sure yet';

      if (format === 'buffet') {
        const b = menu.buffet;
        enforceLimit('buffet-meats', b.meats, true);
        enforceLimit('buffet-sides', b.sides, true);
        pkg = `${b.label} (${money(b.price)}/person)`;
        lines.push([`${b.label} × ${guests || '?'} guests`, guests ? b.price * guests : null]);
        summary.push(`Format: ${b.label} at ${money(b.price)}/person`);
        summary.push(`Meats: ${checked('buffet-meats').join(', ') || 'not chosen yet'}`);
        summary.push(`Sides: ${checked('buffet-sides').join(', ') || 'not chosen yet'}`);
      } else if (format === 'plates') {
        const tier = menu.plates.tiers[parseInt($('[data-tier="plates"]', root).value, 10)];
        enforceLimit('plate-meats', tier.meats);
        enforceLimit('plate-sides', tier.sides);
        pkg = `Plates: ${tier.label} (${money(tier.price)}/person)`;
        lines.push([`${tier.label} × ${guests || '?'} guests`, guests ? tier.price * guests : null]);
        summary.push(`Format: Plates, ${tier.label} at ${money(tier.price)}/person`);
        summary.push(`Meats: ${checked('plate-meats').join(', ') || 'not chosen yet'}`);
        summary.push(`Sides: ${checked('plate-sides').join(', ') || 'not chosen yet'}`);
        if (guests && guests < menu.plates.min) warnings.push(`Plates have a ${menu.plates.min}-guest minimum.`);
      } else if (format === 'boxed') {
        const tier = menu.boxed.tiers[parseInt($('[data-tier="boxed"]', root).value, 10)];
        $('[data-boxed-sides]', root).hidden = tier.sides === 0;
        enforceLimit('boxed-sides', tier.sides);
        pkg = `Boxed lunches: ${tier.label} (${money(tier.price)}/person)`;
        lines.push([`${tier.label} × ${guests || '?'} guests`, guests ? tier.price * guests : null]);
        summary.push(`Format: Boxed lunches, ${tier.label} at ${money(tier.price)}/person`);
        summary.push(`Sandwiches: ${checked('boxed-sandwiches').join(', ') || 'not chosen yet'}`);
        if (tier.sides) summary.push(`Sides: ${checked('boxed-sides').join(', ') || 'not chosen yet'}`);
        if (guests && guests < menu.boxed.min) warnings.push(`Boxed lunches have a ${menu.boxed.min}-guest minimum.`);
      } else if (format === 'boxes') {
        const picked = qtys('boxes', menu.boxes);
        pkg = picked.length ? picked.map((x) => `${x.n} × ${x.label}`).join(', ') : 'Feast/Slider Boxes';
        picked.forEach((x) => lines.push([`${x.n} × ${x.label}`, x.n * x.item.price]));
        summary.push(`Format: Boxes, ${picked.map((x) => `${x.n} × ${x.label}`).join(', ') || 'quantities not chosen yet'}`);
        summary.push(`Meats: ${checked('box-meats').join(', ') || 'not chosen yet'}`);
        if (picked.some((x) => x.item.id === 'feast')) summary.push(`Feast Box sides: ${checked('box-sides').join(', ') || 'not chosen yet'}`);
      } else if (format === 'alacarte') {
        const meats = qtys('alacarte', menu.alacarte);
        meats.forEach((x) => lines.push([`${x.n} ${x.item.unit} ${x.label}`, x.n * x.item.price]));
        pkg = 'À la carte';
        summary.push('Format: À la carte');
        summary.push(`Meats: ${meats.map((x) => `${x.n} ${x.item.unit} ${x.label}`).join(', ') || 'not chosen yet'}`);
      } else {
        summary.push("Format: Not sure yet (would like help choosing)");
      }

      // Add-ons: every line carries its quantity and price so the catering
      // team sees exactly what was asked for.
      const addons = [];
      const addLine = (label, n, unitPrice, unitLabel) => {
        const amt = n * unitPrice;
        lines.push([`${n} × ${label} (${money(unitPrice)}/${unitLabel})`, amt]);
        addons.push(`${n} × ${label} @ ${money(unitPrice)}/${unitLabel} = ${money(amt)}`);
      };
      qtys('addonMeats', menu.addonMeats).forEach((x) => addLine(x.label, x.n, x.item.price, x.item.unit));
      qtys('sidepans', menu.sides, (side) => side).forEach((x) => addLine(`Extra side: ${x.label}`, x.n, menu.sidePan.price, menu.sidePan.unit));
      const trays = qtys('trays', menu.trays.items);
      trays.forEach((x) => addLine(x.label, x.n, x.item.price, 'person'));
      const minTray = trays.filter((x) => !x.item.noMin && x.n < menu.trays.min);
      if (minTray.length) warnings.push(`Trays have a ${menu.trays.min}-person minimum (${minTray.map((x) => x.label).join(', ')}).`);
      qtys('desserts', menu.desserts).forEach((x) => addLine(x.label, x.n, x.item.price, x.item.unit));
      qtys('drinks', menu.drinks).forEach((x) => addLine(x.label, x.n, x.item.price, x.item.unit));
      if (addons.length) summary.push(`Add-ons:\n${addons.map((a) => `- ${a}`).join('\n')}`);

      const priced = lines.filter(([, amt]) => amt !== null);
      const total = priced.reduce((sum, [, amt]) => sum + amt, 0);
      const needsGuests = lines.some(([, amt]) => amt === null);

      const daysOut = businessDaysUntil(dateInput && dateInput.value);
      const rush = daysOut !== null && daysOut < 7;
      if (rush) warnings.unshift('Rush request: event is less than 7 business days out.');
      const rushHtml = rush ? `<p class="event-builder__warn"><strong>Your date is less than 7 business days away.</strong> For urgent requests, please call us now at ${callLink} so we can confirm availability.</p>` : '';
      if (!lines.length) {
        panel.innerHTML = `<p class="muted">Your estimate will show here as you choose.</p>${rushHtml}`;
      } else {
        panel.innerHTML = `
          <ul class="event-builder__lines">${lines.map(([label, amt]) => `<li><span>${esc(label)}</span><span>${amt === null ? '—' : money(amt)}</span></li>`).join('')}</ul>
          <p class="event-builder__total"><span>Estimated food total</span><strong>${money(total)}</strong></p>
          ${needsGuests ? '<p class="muted">Add your guest count above to price per-person items.</p>' : ''}
          ${rushHtml}${warnings.filter((w) => !w.startsWith('Rush request')).map((w) => `<p class="event-builder__warn">${esc(w)}</p>`).join('')}
          <p class="muted event-builder__fine">Estimate only, before tax, delivery, and staffing. Our catering team confirms the final quote. Menu and guest count are due 7 business days before your event; for urgent requests, please call us now at ${callLink}.</p>`;
      }

      if (lines.length) {
        summary.push(`Estimate breakdown (guests: ${guests || 'not set'}):\n${lines.map(([label, amt]) => `- ${label}: ${amt === null ? 'needs guest count' : money(amt)}`).join('\n')}\nEstimated food total: ${money(total)}${needsGuests ? ' (plus per-person items once guest count is set)' : ''}`);
      }
      if (warnings.length) summary.push(`Notes: ${warnings.join(' ')}`);

      pkgInput.value = pkg.slice(0, 120);
      selInput.value = summary.join('\n').slice(0, 4000);
      estInput.value = lines.length ? `${money(total)}${needsGuests ? ' (plus per-person items once guest count is set)' : ''}` : '';
    }

    // "All guests" on per-person add-ons fills the quantity with the guest count.
    root.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-fill]');
      if (!btn) return;
      const input = $(`input[data-fill-id="${btn.dataset.fill}"]`, root);
      const guests = Math.max(0, parseInt((guestsInput && guestsInput.value) || '0', 10) || 0);
      if (!input) return;
      if (!guests && guestsInput) { guestsInput.focus(); return; }
      input.value = String(guests);
      update();
    });
    root.addEventListener('change', update);
    root.addEventListener('input', update);
    if (guestsInput) guestsInput.addEventListener('input', update);
    if (dateInput) dateInput.addEventListener('change', update);
    form.addEventListener('lb:form-success', () => {
      $$('input[type="checkbox"]', root).forEach((el) => { el.checked = false; el.disabled = false; });
      $$('input[data-qty]', root).forEach((el) => { el.value = '0'; });
      const unsure = $('input[data-format][value="unsure"]', root);
      if (unsure) unsure.checked = true;
      update();
    });
    update();
  }

  /* ---- Build-your-event modal (catering pages) ---- */
  function initEventBuilderModals() {
    $$('[data-event-builder-modal]').forEach((modal) => {
      const form = $('form', modal);
      wireModalDismiss(modal);
      initCateringBuilder(modal);
      const afterClose = () => {
        if (!modal.__lbWasOpen) return;
        setTimeout(() => {
          if (!modal.hidden) return;
          modal.__lbWasOpen = false;
          requestFormRecovery(form);
        }, 250);
      };
      $$('[data-modal-close]', modal).forEach((el) => el.addEventListener('click', afterClose));
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') afterClose(); });
      const id = modal.id;
      $$(`[data-open-event-builder="${id}"]`).forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          openModal(modal, btn);
          modal.__lbWasOpen = true;
          trackEvent('event_builder_open', { page_path: window.location.pathname });
        });
      });
    });
  }

  /* ---- Ecommerce tracking: view_item / add_to_cart ----
     Covers both product templates (the generic one and the cow-share one)
     via known selectors rather than a data-attribute, since Shopify's
     {% form 'product', product %} tag doesn't cleanly accept custom
     data-* params. Reads price/title straight from the DOM so it always
     matches whatever variant is currently selected, no separate JSON blob
     to keep in sync. */
  const PRODUCT_FORM_SELECTOR = '#product-form, .cs-product__form';

  function currentProductItem(form) {
    const root = form.closest('.product-page, .cs-product') || document;
    const titleEl = $('.prod-body h1, .cs-product__title', root);
    const priceEl = $('[data-prod-price-current], .cs-product__price', root);
    const idEl = $('input[name="id"]', form);
    const qtyEl = $('input[name="quantity"]', form);
    const priceNum = priceEl ? parseFloat(priceEl.textContent.replace(/[^0-9.]/g, '')) : undefined;
    return {
      item_id: idEl ? idEl.value : undefined,
      item_name: titleEl ? titleEl.textContent.trim() : undefined,
      price: isNaN(priceNum) ? undefined : priceNum,
      quantity: qtyEl ? (parseInt(qtyEl.value, 10) || 1) : 1
    };
  }

  function initProductViewTracking() {
    const form = $(PRODUCT_FORM_SELECTOR);
    if (!form) return;
    const item = currentProductItem(form);
    trackEvent('view_item', {
      currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD',
      value: item.price,
      items: [item]
    });
  }

  function initAddToCartTracking() {
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement) || !form.matches(PRODUCT_FORM_SELECTOR)) return;
      const item = currentProductItem(form);
      trackEvent('add_to_cart', {
        currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD',
        value: (item.price || 0) * item.quantity,
        items: [item]
      });
    }, true);
  }

  /* ---- Smooth anchor scroll respecting reduced motion ---- */
  function initAnchors() {
    document.addEventListener('click', (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (!a) return;
      const id = a.getAttribute('href').slice(1);
      if (!id) return;
      const tgt = document.getElementById(id);
      if (!tgt) return;
      e.preventDefault();
      tgt.scrollIntoView({ behavior: prefersReduced ? 'auto' : 'smooth', block: 'start' });
      tgt.setAttribute('tabindex', '-1');
      tgt.focus({ preventScroll: true });
    });
  }

  /* ---- Event month filters ---- */
  function initEventFilters() {
    $$('[data-events-section]').forEach((section) => {
      const cards = $$('[data-event-card]', section);
      const filters = $$('[data-event-month]', section);
      if (!cards.length || !filters.length) return;

      const monthsWithEvents = new Set(cards.map((card) => (card.dataset.month || '').toUpperCase()).filter(Boolean));
      filters.forEach((filter) => {
        const month = (filter.dataset.eventMonth || '').toUpperCase();
        if (month !== 'ALL' && !monthsWithEvents.has(month)) filter.classList.add('is-empty');
      });

      filters.forEach((filter) => {
        filter.addEventListener('click', () => {
          const selected = (filter.dataset.eventMonth || 'all').toUpperCase();
          filters.forEach((btn) => btn.classList.toggle('is-active', btn === filter));
          cards.forEach((card) => {
            const cardMonth = (card.dataset.month || '').toUpperCase();
            card.classList.toggle('is-hidden', selected !== 'ALL' && cardMonth !== selected);
          });
          const pastHeading = $('[data-events-past-heading]', section);
          if (pastHeading) pastHeading.classList.toggle('is-hidden', !cards.some((card) => card.classList.contains('event-card--past') && !card.classList.contains('is-hidden')));
        });
      });
    });
  }

  /* ---- Menu category filters ---- */
  function initMenuTabs() {
    $$('[data-menu-section]').forEach((section) => {
      const buttons = $$('[data-menu-filter]', section);
      const categories = $$('[data-menu-category]', section);
      if (!buttons.length || !categories.length) return;

      buttons.forEach((button) => {
        button.addEventListener('click', () => {
          const filter = button.dataset.menuFilter || 'all';
          buttons.forEach((btn) => {
            const active = btn === button;
            btn.classList.toggle('is-active', active);
            btn.setAttribute('aria-selected', String(active));
          });
          categories.forEach((category) => {
            category.classList.toggle('is-hidden', filter !== 'all' && category.dataset.menuCategory !== filter);
          });
        });
      });
    });
  }

  /* ---- Menu board deep links (?section=appetizers or #appetizers) ---- */
  function initMenuBoardDeepLinks() {
    const boards = $$('[data-menu-board]');
    if (!boards.length) return;

    const slugify = (value) => String(value || '')
      .trim()
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    const params = new URLSearchParams(window.location.search);
    const requested = window.location.hash
      ? decodeURIComponent(window.location.hash.slice(1))
      : (params.get('section') || params.get('open') || params.get('category') || params.get('menu'));
    if (!requested) return;

    const wanted = slugify(requested);
    let target = null;
    boards.some((board) => {
      target = $$('[data-menu-board-category]', board).find((category) => {
        const id = slugify(category.id);
        const anchor = slugify(category.dataset.menuAnchor);
        const heading = slugify(($('h3', category) || {}).textContent);
        return wanted === id || wanted === anchor || wanted === heading;
      });
      return Boolean(target);
    });
    if (!target) return;

    const activate = () => {
      $$('[data-menu-board-category]').forEach((category) => category.classList.remove('is-targeted'));
      target.classList.add('is-targeted');
      target.scrollIntoView({ behavior: prefersReduced ? 'auto' : 'smooth', block: 'start' });
      target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    };

    if (document.readyState === 'complete') {
      activate();
    } else {
      window.setTimeout(activate, 120);
    }
  }

  /* ---- Catering package + build-your-own modals ---- */
  /* "Build your own menu" checklist: the full catering menu, from the same
     [data-catering-menu] JSON the Build Your Event builder uses, so both list
     every item and price. The Liquid-rendered builder_item list stays as a
     fallback if the data isn't on the page. */
  function renderMenuBuilderChoices(section) {
    const dataEl = $('[data-catering-menu]');
    const nav = $('.catering-builder__nav ul', section);
    const items = $('.catering-builder__items', section);
    if (!dataEl || !nav || !items) return;
    let menu;
    try { menu = JSON.parse(dataEl.textContent); } catch (e) { return; }
    const esc = escapeHtml;
    const money = (n) => `$${Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
    const groups = [
      [`Plates`, `Per person, minimum ${menu.plates.min}`, menu.plates.tiers.map((t) => [`Plate: ${t.label}`, `${money(t.price)} / person`])],
      [`Boxed lunches`, `Per person, minimum ${menu.boxed.min}`, menu.boxed.tiers.map((t) => [`Boxed lunch: ${t.label}`, `${money(t.price)} / person`])
        .concat(menu.boxed.sandwiches.map((n) => [`Sandwich: ${n}`, ''])) ],
      [`Meats`, `For plates, boxed lunches, and boxes`, menu.meats.map((n) => [n, ''])],
      [`Sides`, `For plates, boxed lunches, and Feast Boxes`, menu.sides.map((n) => [n, ''])],
      [`Feast & Slider Boxes`, `Feeds a crowd`, menu.boxes.map((b) => [b.label, `${money(b.price)} · ${b.desc}`])],
      [`Meats by the pound`, `À la carte`, menu.alacarte.map((m) => [`${m.label} (à la carte)`, `${money(m.price)} / ${m.unit}`])],
      [`Sides by the ½ pan`, `À la carte`, menu.sides.map((n) => [`${n}, ½ pan`, money(menu.sidePan)])],
      [`Trays & extras`, `Per person, minimum ${menu.trays.min}`, menu.trays.items.map((t) => [t.label, `${money(t.price)} / person`])],
      [`Desserts`, '', menu.desserts.map((d) => [d.label, `${money(d.price)} / ${d.unit}`])],
      [`Beverages`, '', menu.drinks.map((d) => [d.label, `${money(d.price)} / ${d.unit}`])],
    ];
    const slug = (t) => 'builder-' + t.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    nav.innerHTML = groups.map(([title]) => `<li><a href="#${slug(title)}">${esc(title)}</a></li>`).join('');
    items.innerHTML = groups.map(([title, note, list]) => `
      <div class="catering-builder__group" id="${slug(title)}">
        <h3 class="h5">${esc(title)}${note ? ` <small class="muted">${esc(note)}</small>` : ''}</h3>
        ${list.map(([label, price]) => `
          <label class="catering-builder__choice">
            <input type="checkbox" value="${esc(price ? `${label} (${price.split(' · ')[0]})` : label)}" data-catering-builder-choice>
            <span>${esc(label)}${price ? ` <small class="muted">${esc(price)}</small>` : ''}</span>
          </label>`).join('')}
      </div>`).join('');
  }

  function initCateringExperience() {
    $$('[data-catering-experience]').forEach((section) => {
      const modals = $$('[data-catering-modal]', section);
      if (!modals.length) return;
      renderMenuBuilderChoices(section);

      let activeModal = null;
      let lastTrigger = null;

      const closeModal = () => {
        if (!activeModal) return;
        activeModal.hidden = true;
        document.documentElement.classList.remove('has-modal-open');
        activeModal = null;
        if (lastTrigger) lastTrigger.focus({ preventScroll: true });
      };

      const openModal = (id, trigger) => {
        const modal = document.getElementById(id);
        if (!modal) return;
        if (activeModal && activeModal !== modal) closeModal();
        lastTrigger = trigger || document.activeElement;
        activeModal = modal;
        modal.hidden = false;
        document.documentElement.classList.add('has-modal-open');
        const dialog = $('[role="dialog"]', modal);
        window.setTimeout(() => dialog && dialog.focus({ preventScroll: true }), 20);
      };

      const applyPackageToInquiry = (packageName) => {
        if (!packageName) return;
        const select = document.getElementById('CatPackage');
        const notes = document.getElementById('CatNotes');
        if (select) {
          const matching = Array.from(select.options).find((option) => option.value === packageName || option.textContent.includes(packageName));
          if (matching) select.value = matching.value;
        }
        if (notes && !notes.value.includes(packageName)) {
          notes.value = [notes.value, `Package interest: ${packageName}`].filter(Boolean).join('\n');
        }
      };

      $$('[data-catering-modal-open]', section).forEach((trigger) => {
        trigger.addEventListener('click', () => {
          const packageName = trigger.dataset.cateringPackage;
          if (packageName) applyPackageToInquiry(packageName);
          openModal(trigger.dataset.cateringModalOpen, trigger);
        });
      });

      $$('[data-catering-modal-close]', section).forEach((trigger) => {
        trigger.addEventListener('click', () => {
          const packageName = trigger.dataset.cateringPackage;
          if (packageName) applyPackageToInquiry(packageName);
          closeModal();
        });
      });

      $$('[data-catering-builder-add]', section).forEach((button) => {
        button.addEventListener('click', () => {
          const choices = $$('[data-catering-builder-choice]:checked', section).map((choice) => choice.value);
          const notes = document.getElementById('CatNotes');
          if (!choices.length || !notes) return;
          const text = `Build-your-own menu selections: ${choices.join(', ')}`;
          notes.value = notes.value ? `${notes.value}\n${text}` : text;
          const form = document.getElementById('catering-inquiry');
          form && window.setTimeout(() => form.scrollIntoView({ behavior: prefersReduced ? 'auto' : 'smooth', block: 'start' }), 80);
        });
      });

      $$('[data-catering-builder-clear]', section).forEach((button) => {
        button.addEventListener('click', () => {
          $$('[data-catering-builder-choice]', section).forEach((choice) => { choice.checked = false; });
        });
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') closeModal();
      });
    });
  }

  /* ---- Quote rotators ---- */
  function initQuoteRotators() {
    $$('[data-quote-rotator]').forEach((rotator) => {
      const slides = $$('[data-quote-slide]', rotator);
      const dots = $$('[data-quote-dot]', rotator);
      if (slides.length < 2) return;
      let idx = 0;
      const goTo = (next) => {
        slides[idx].classList.remove('is-active');
        if (dots[idx]) { dots[idx].classList.remove('is-active'); dots[idx].setAttribute('aria-selected', 'false'); }
        idx = next;
        slides[idx].classList.add('is-active');
        if (dots[idx]) { dots[idx].classList.add('is-active'); dots[idx].setAttribute('aria-selected', 'true'); }
      };
      dots.forEach((dot, i) => dot.addEventListener('click', () => goTo(i)));
      if (prefersReduced) return;
      setInterval(() => goTo((idx + 1) % slides.length), 5500);
    });
  }

  /* ---- Generic modal open/close helper ---- */
  function openModal(modal, trigger) {
    if (!modal) return;
    modal.__lastTrigger = trigger || document.activeElement;
    modal.hidden = false;
    document.documentElement.classList.add('has-modal-open');
    const dialog = $('[role="dialog"]', modal) || modal;
    setTimeout(() => dialog.focus && dialog.focus({ preventScroll: true }), 20);
  }
  function closeModal(modal) {
    if (!modal || modal.hidden) return;
    modal.hidden = true;
    document.documentElement.classList.remove('has-modal-open');
    if (modal.__lastTrigger && modal.__lastTrigger.focus) modal.__lastTrigger.focus({ preventScroll: true });
    modal.dispatchEvent(new CustomEvent('lb:modal-closed', { bubbles: true }));
  }
  function wireModalDismiss(modal) {
    $$('[data-modal-close]', modal).forEach((el) => el.addEventListener('click', () => closeModal(modal)));
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeModal(modal); });
  }

  /* ---- Event detail modals ---- */
  function initEventModals() {
    $$('[data-events-section]').forEach((section) => {
      const modal = $('[data-event-modal]', section);
      if (!modal) return;
      wireModalDismiss(modal);

      const mediaEl = $('[data-modal-media]', modal);
      const catEl = $('[data-modal-category]', modal);
      const monthEl = $('[data-modal-month]', modal);
      const dayEl = $('[data-modal-day]', modal);
      const titleEl = $('[data-modal-title]', modal);
      const subEl = $('[data-modal-subtitle]', modal);
      const contentEl = $('[data-modal-content]', modal);
      const goingEl = $('[data-modal-going]', modal);
      const fbEl = $('[data-modal-fb]', modal);
      const actionsEl = $('[data-modal-actions]', modal);

      $$('[data-event-trigger]', section).forEach((trigger) => {
        trigger.addEventListener('click', () => {
          const d = trigger.dataset;
          const card = trigger.closest('[data-event-card]');
          const full = card ? $('.event-card__full', card) : null;

          mediaEl.innerHTML = d.image ? `<img src="${escapeHtml(d.image)}" alt="${escapeHtml(d.title)}">` : '';
          if (d.category) { catEl.textContent = d.category; catEl.hidden = false; } else { catEl.hidden = true; }
          monthEl.textContent = d.month || '';
          dayEl.textContent = d.day || '';
          titleEl.textContent = d.title || '';
          subEl.textContent = d.subtitle || '';
          contentEl.innerHTML = full ? full.innerHTML : '';

          // "Are you going?" Facebook link
          if (d.fb) { fbEl.href = d.fb; goingEl.hidden = false; } else { goingEl.hidden = true; }

          // Action buttons
          let actions = '';
          if (d.rsvp) actions += `<a class="btn btn--primary" href="${escapeHtml(d.rsvp)}" target="_blank" rel="noopener">Get tickets</a>`;
          if (d.fb) actions += `<a class="btn btn--ghost" href="${escapeHtml(d.fb)}" target="_blank" rel="noopener">RSVP on Facebook</a>`;
          const calendar = buildCalendarData({
            title: d.title,
            subtitle: d.subtitle,
            description: d.description || contentEl.innerHTML,
            month: d.month,
            day: d.day,
            calendarDate: d.calendarDate,
            calendarStart: d.calendarStart,
            calendarEnd: d.calendarEnd,
            location: d.location,
            url: d.rsvp || d.fb || window.location.href,
          });
          if (calendar) actions += `<a class="btn btn--ghost" href="${calendar.href}" download="${escapeHtml(calendar.filename)}">Add to calendar</a>`;
          actionsEl.innerHTML = actions;

          openModal(modal, trigger);
        });
      });
    });
  }

  /* ---- Static Add to calendar links ---- */
  function initCalendarLinks() {
    $$('[data-event-calendar-link]').forEach((link) => {
      const calendar = buildCalendarData({
        title: link.dataset.title,
        description: link.dataset.description,
        month: link.dataset.month,
        day: link.dataset.day,
        calendarDate: link.dataset.calendarDate,
        calendarStart: link.dataset.calendarStart,
        calendarEnd: link.dataset.calendarEnd,
        location: link.dataset.location,
        url: link.dataset.url || window.location.href,
        filename: link.dataset.filename,
      });
      if (!calendar) {
        link.hidden = true;
        return;
      }
      link.href = calendar.href;
      link.download = calendar.filename;
      link.addEventListener('click', () => {
        trackEvent('add_to_calendar', { event_title: link.dataset.title || '' });
      });
    });
  }

  /* ---- Share buttons (native share sheet, copy-link fallback) ---- */
  function initShareButtons() {
    $$('[data-share-url]').forEach((btn) => {
      const label = btn.textContent;
      btn.addEventListener('click', async () => {
        const url = new URL(btn.dataset.shareUrl || window.location.href, window.location.origin).href;
        const title = btn.dataset.shareTitle || document.title;
        trackEvent('share', { content_title: title });
        if (navigator.share) {
          try { await navigator.share({ title, url }); } catch (e) { /* user cancelled */ }
          return;
        }
        try {
          await navigator.clipboard.writeText(url);
          btn.textContent = 'Link copied';
        } catch (e) {
          window.prompt('Copy this link:', url);
          return;
        }
        setTimeout(() => { btn.textContent = label; }, 2000);
      });
    });
  }

  /* ---- Linkable placeholder/fallback event detail state ---- */
  function initEventQueryDetails() {
    const params = new URLSearchParams(window.location.search);
    const slug = params.get('event');
    if (!slug) return;

    const card = $$('[data-event-slug]').find((el) => el.dataset.eventSlug === slug);
    if (!card) return;

    const section = card.closest('[data-events-section]');
    const container = section && $('.container', section);
    if (!section || !container || $('.event-query-detail', section)) return;

    const d = card.dataset;
    const media = $('.event-card__media', card);
    const calendar = buildCalendarData({
      title: d.title,
      subtitle: d.subtitle,
      description: d.description,
      month: d.month,
      day: d.day,
      calendarDate: d.calendarDate,
      calendarStart: d.calendarStart,
      location: d.location,
      url: d.url || window.location.href,
    });
    const mediaMarkup = media ? media.innerHTML : '<div class="event-card__placeholder" aria-hidden="true"></div>';
    const backUrl = `${window.location.pathname}`;
    const calendarButton = calendar
      ? `<a class="btn btn--ghost" href="${calendar.href}" download="${escapeHtml(calendar.filename)}">Add to calendar</a>`
      : '';
    const facebookButton = d.fb
      ? `<a class="btn btn--ghost" href="${escapeHtml(d.fb)}" target="_blank" rel="noopener">Facebook event</a>`
      : '';

    const detail = document.createElement('article');
    detail.className = 'event-query-detail';
    detail.innerHTML = `
      <div class="event-query-detail__media">${mediaMarkup}</div>
      <div class="event-query-detail__body">
        ${d.category ? `<p class="event-card__category">${escapeHtml(d.category)}</p>` : ''}
        <h1 class="h2">${escapeHtml(d.title || 'Event details')}</h1>
        <p class="muted">${escapeHtml(d.subtitle || '')}</p>
        <p class="lead">${escapeHtml(d.description || '')}</p>
        <div class="cluster">
          ${calendarButton}
          ${facebookButton}
          <a class="btn btn--ghost" href="${escapeHtml(backUrl)}">View all events</a>
        </div>
      </div>
    `;

    section.classList.add('is-event-detail-mode');
    container.prepend(detail);
    document.title = `${d.title || 'Event details'} | ${document.title.replace(/^.*? \| /, '')}`;
  }

  /* ---- Gallery carousel arrows ---- */
  function initGalleryCarousel() {
    $$('[data-gallery-carousel]').forEach((wrap) => {
      const track = $('[data-gallery-track]', wrap);
      const prev = $('[data-gallery-prev]', wrap);
      const next = $('[data-gallery-next]', wrap);
      if (!track) return;
      const step = () => Math.max(track.clientWidth * 0.8, 240);
      const update = () => {
        if (!prev || !next) return;
        prev.disabled = track.scrollLeft <= 4;
        next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
      };
      prev && prev.addEventListener('click', () => { track.scrollBy({ left: -step(), behavior: prefersReduced ? 'auto' : 'smooth' }); });
      next && next.addEventListener('click', () => { track.scrollBy({ left: step(), behavior: prefersReduced ? 'auto' : 'smooth' }); });
      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }

  /* ---- Menu gallery strip: randomizes on load, follows the menu category in view ---- */
  function initMenuGallery() {
    const wrap = $('[data-menu-gallery]');
    if (!wrap) return;
    const items = $$('[data-menu-gallery-item]', wrap);
    if (!items.length) return;
    const visibleCount = 5;

    const categoriesOf = (el) => (el.dataset.categories || '').split(',').map((s) => s.trim()).filter(Boolean);

    const shuffle = (arr) => {
      const a = arr.slice();
      for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
      }
      return a;
    };

    const applySelection = (selected) => {
      const chosen = new Set(selected);
      items.forEach((el) => {
        if (chosen.has(el)) {
          el.classList.add('is-active');
          el.classList.remove('is-hidden');
        } else {
          el.classList.add('is-hidden');
          el.classList.remove('is-active');
        }
      });
    };

    const pickForCategory = (category) => {
      const matches = category ? shuffle(items.filter((el) => categoriesOf(el).includes(category))) : shuffle(items);
      if (matches.length >= visibleCount) return matches.slice(0, visibleCount);
      const fillers = shuffle(items.filter((el) => !matches.includes(el)));
      return matches.concat(fillers).slice(0, visibleCount);
    };

    applySelection(pickForCategory(null));

    const categories = $$('[data-menu-board-category]');
    if (categories.length && 'IntersectionObserver' in window) {
      let current = null;
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const anchor = entry.target.dataset.menuAnchor;
          if (anchor && anchor !== current) {
            current = anchor;
            if (prefersReduced) {
              applySelection(pickForCategory(anchor));
            } else {
              wrap.classList.add('is-swapping');
              window.setTimeout(() => {
                applySelection(pickForCategory(anchor));
                wrap.classList.remove('is-swapping');
              }, 220);
            }
          }
        });
      }, { threshold: 0.35, rootMargin: '-20% 0px -40% 0px' });
      categories.forEach((cat) => io.observe(cat));
    }
  }

  /* ---- Image lightbox (galleries + page imagery) ---- */
  function initLightbox() {
    // Build one shared lightbox element
    const box = document.createElement('div');
    box.className = 'lb-lightbox';
    box.hidden = true;
    box.setAttribute('role', 'dialog');
    box.setAttribute('aria-modal', 'true');
    box.setAttribute('aria-label', 'Image viewer');
    box.tabIndex = -1;
    box.innerHTML = `
      <button class="lb-lightbox__btn lb-lightbox__close" type="button" aria-label="Close">✕</button>
      <button class="lb-lightbox__btn lb-lightbox__btn--prev" type="button" aria-label="Previous image">‹</button>
      <figure class="lb-lightbox__figure">
        <img class="lb-lightbox__img" alt="">
        <figcaption class="lb-lightbox__caption">
          <span class="lb-lightbox__title" data-lightbox-title></span>
          <span class="lb-lightbox__desc" data-lightbox-desc></span>
        </figcaption>
      </figure>
      <button class="lb-lightbox__btn lb-lightbox__btn--next" type="button" aria-label="Next image">›</button>
      <div class="lb-lightbox__count"></div>`;
    document.body.appendChild(box);
    const imgEl = $('.lb-lightbox__img', box);
    const countEl = $('.lb-lightbox__count', box);
    const titleEl = $('[data-lightbox-title]', box);
    const descEl = $('[data-lightbox-desc]', box);
    const closeBtn = $('.lb-lightbox__close', box);

    let items = [];
    let idx = 0;
    let lastTrigger = null;
    const show = (i) => {
      idx = (i + items.length) % items.length;
      const item = items[idx];
      imgEl.src = item.full;
      imgEl.alt = item.alt || '';
      titleEl.textContent = item.title || '';
      titleEl.hidden = !item.title;
      descEl.textContent = item.caption || '';
      descEl.hidden = !item.caption;
      countEl.textContent = items.length > 1 ? `${idx + 1} / ${items.length}` : '';
      box.setAttribute('aria-label', item.title || item.alt || 'Image viewer');
    };
    const open = (galleryItems, startIdx, trigger) => {
      items = galleryItems;
      lastTrigger = trigger || document.activeElement;
      box.hidden = false;
      document.documentElement.classList.add('has-modal-open');
      show(startIdx);
      closeBtn.focus({ preventScroll: true });
    };
    const close = () => {
      if (box.hidden) return;
      box.hidden = true;
      document.documentElement.classList.remove('has-modal-open');
      if (lastTrigger && lastTrigger.focus) lastTrigger.focus({ preventScroll: true });
    };

    closeBtn.addEventListener('click', close);
    $('.lb-lightbox__btn--prev', box).addEventListener('click', () => show(idx - 1));
    $('.lb-lightbox__btn--next', box).addEventListener('click', () => show(idx + 1));
    box.addEventListener('click', (e) => { if (e.target === box) close(); });
    document.addEventListener('keydown', (e) => {
      if (box.hidden) return;
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowLeft') show(idx - 1);
      if (e.key === 'ArrowRight') show(idx + 1);
      if (e.key === 'Tab') {
        const focusable = $$('button', box);
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    });

    $$('[data-lightbox-gallery]').forEach((gallery) => {
      const figures = $$('[data-lightbox-item]', gallery);
      const data = figures.map((fig) => {
        const img = $('img', fig);
        return {
          full: (img && (img.dataset.full || img.src)) || '',
          alt: (img && img.alt) || '',
          title: fig.dataset.title || '',
          caption: fig.dataset.caption || '',
        };
      }).filter((d) => d.full);
      figures.forEach((fig, i) => {
        const img = $('img', fig);
        if (!img) return;
        fig.setAttribute('role', 'button');
        fig.setAttribute('tabindex', '0');
        if (!fig.getAttribute('aria-label')) {
          fig.setAttribute('aria-label', `View larger image${data[i] && data[i].title ? ': ' + data[i].title : ''}`);
        }
        fig.addEventListener('click', () => open(data, i, fig));
        fig.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(data, i, fig); }
        });
      });
    });

    $$('main img, .shopify-section img').forEach((img) => {
      if (!img.src) return;
      if (img.closest('a, button, [data-lightbox-gallery], .site-header, header, footer, .footer-main, .nav, .lb-lightbox')) return;
      if (img.closest('.no-lightbox') || img.dataset.lightboxSkip === 'true') return;
      img.classList.add('is-lightboxable');
      img.setAttribute('role', 'button');
      img.setAttribute('tabindex', '0');
      const openThis = () => {
        open([{ full: img.dataset.full || img.currentSrc || img.src, alt: img.alt || '', title: img.title || '' }], 0, img);
      };
      img.addEventListener('click', openThis);
      img.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openThis(); }
      });
    });
  }

  /* ---- Animated details/summary accordions ---- */
  function initAnimatedDetails() {
    $$('.faq-item, .catering-faq').forEach((details) => {
      const summary = $('summary', details);
      const content = $('.faq-a, .rte', details);
      if (!summary || !content || details.dataset.accordionReady === 'true') return;
      details.dataset.accordionReady = 'true';
      content.style.overflow = 'hidden';

      if (!details.open) {
        content.style.height = '0px';
        content.style.opacity = '0';
      }

      summary.addEventListener('click', (e) => {
        e.preventDefault();
        if (prefersReduced) {
          details.open = !details.open;
          content.style.height = details.open ? '' : '0px';
          content.style.opacity = details.open ? '1' : '0';
          return;
        }

        const isOpen = details.open;
        if (isOpen) {
          const startHeight = content.offsetHeight;
          content.style.height = `${startHeight}px`;
          content.style.opacity = '1';
          requestAnimationFrame(() => {
            content.style.transition = 'height 260ms var(--ease), opacity 180ms var(--ease)';
            content.style.height = '0px';
            content.style.opacity = '0';
          });
          window.setTimeout(() => {
            details.open = false;
            content.style.transition = '';
          }, 270);
        } else {
          details.open = true;
          content.style.height = '0px';
          content.style.opacity = '0';
          const endHeight = content.scrollHeight;
          requestAnimationFrame(() => {
            content.style.transition = 'height 260ms var(--ease), opacity 180ms var(--ease)';
            content.style.height = `${endHeight}px`;
            content.style.opacity = '1';
          });
          window.setTimeout(() => {
            content.style.height = '';
            content.style.transition = '';
          }, 270);
        }
      });
    });
  }

  /* ---- Job detail modal + application form (multipart, resume upload) ---- */
  function initJobApply() {
    $$('[data-job-openings]').forEach((section) => {
      const detailModal = $('[data-job-detail-modal]', section);
      if (!detailModal) return;
      wireModalDismiss(detailModal);

      const dTag = $('[data-job-modal-tag]', detailModal);
      const dTitle = $('[data-job-modal-title]', detailModal);
      const dSub = $('[data-job-modal-subtitle]', detailModal);
      const dContent = $('[data-job-modal-content]', detailModal);

      // Card → detail modal. Apply buttons are plain links to jobs.lazybutcher.com.
      $$('[data-job-trigger]', section).forEach((trigger) => {
        trigger.addEventListener('click', () => {
          const card = trigger.closest('[data-job-card]');
          const full = card ? $('.job-card__full', card) : null;
          const d = trigger.dataset;
          if (dTag) dTag.textContent = d.tag || '';
          if (dTitle) dTitle.textContent = d.title || '';
          if (dSub) dSub.textContent = d.subtitle || '';
          if (dContent) dContent.innerHTML = full ? full.innerHTML : '';
          openModal(detailModal, trigger);
        });
      });
    });
  }

  function initScrollDecor() {
    const items = $$('[data-scroll-decor] .scroll-decor__item');
    if (!items.length) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let ticking = false;
    function update() {
      const y = window.scrollY || window.pageYOffset || 0;
      items.forEach((el, i) => {
        const depth = parseFloat(el.dataset.depth) || 0.2;
        const bob = Math.sin(y * 0.0025 + i * 1.3) * 22;
        const rotate = (y * depth * 0.06) % 360;
        el.style.transform = `translateY(${bob.toFixed(1)}px) rotate(${rotate.toFixed(1)}deg)`;
      });
      ticking = false;
    }
    function onScroll() {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    }
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  document.addEventListener('DOMContentLoaded', () => {
    const has = (sel) => Boolean($(sel));
    initThemeToggle();
    initNav();
    prepareRevealPatterns();
    initBleedGridImages();
    initReveal();
    if (has('[data-hero-crossfade]')) initHero();
    if (has('[data-spoton-menu]')) initSpotonMenus();
    if (has('[data-instagram-feed]')) initInstagramFeeds();
    if (has('[data-product-recommendations]')) initProductRecommendations();
    if (has('[data-onload-popup]')) initOnloadPopup();
    if (has('[data-exit-intent-popup]')) initExitIntentPopup();
    if (has('[data-lb-form]')) initForms();
    if (has('[data-event-builder-modal]')) initEventBuilderModals();
    if (has('[data-form-recovery]')) initFormRecovery();
    initAnchors();
    if (has('[data-events-section]')) initEventFilters();
    if (has('[data-menu-section]')) initMenuTabs();
    if (has('[data-menu-board]')) initMenuBoardDeepLinks();
    if (has('[data-catering-experience]')) initCateringExperience();
    if (has('[data-quote-rotator]')) initQuoteRotators();
    if (has('[data-event-modal]')) initEventModals();
    if (has('[data-event-calendar-link]')) initCalendarLinks();
    if (has('[data-share-url]')) initShareButtons();
    if (has('[data-event-slug]')) initEventQueryDetails();
    if (has('[data-gallery-carousel]')) initGalleryCarousel();
    if (has('[data-menu-gallery]')) initMenuGallery();
    if (has('[data-lightbox-gallery], main img, .shopify-section img')) initLightbox();
    if (has('.faq-item, .catering-faq')) initAnimatedDetails();
    if (has('[data-job-openings]')) initJobApply();
    if (has('[data-scroll-decor]')) initScrollDecor();
    if (has('#product-form, .cs-product__form')) {
      initProductViewTracking();
      initAddToCartTracking();
    }
  });
})();
